import { html, nothing } from "lit";
import type { ChatTabLabelHint } from "../chat/chat-tab-labels.ts";
import type { SessionTabActivityMap } from "../chat/session-tab-activity.ts";
import type { SessionsListResult } from "../types.ts";
import type { ChatSessionContextMeta } from "./chat-session-context.ts";
import type { SessionShareFormat } from "./session-share.ts";
import { resolveFleetSessionBadge } from "../chat/fleet-session-badge.ts";
import { FLEET_CHAT_FOCUS_HINTS } from "../control-hints.ts";
import { icons } from "../icons.ts";
import { renderChatSessionTabs } from "./chat-session-tabs.ts";
import { renderFleetSessionCatalogRow } from "./fleet-session-catalog-row.ts";

export type FleetSessionBarProps = {
  activeSessionKey: string;
  openTabs: string[];
  sessions: SessionsListResult | null;
  connected: boolean;
  labelHints?: Record<string, ChatTabLabelHint>;
  tabMeta?: Record<string, ChatSessionContextMeta>;
  highlightSessionKey?: string | null;
  tabActivity?: SessionTabActivityMap;
  onSelectTab: (sessionKey: string) => void;
  onCloseTab: (sessionKey: string) => void;
  onNewEmptySession: () => void;
  onArchiveAndNew?: () => void;
  onForkTab?: () => void;
  onResetTab?: () => void;
  onDeleteSession?: (sessionKey: string) => void;
  onManageSessions?: () => void;
  onShowArchivedSessions?: () => void;
  onNewAgent?: () => void;
  onOpenAgents?: () => void;
  /** Callback when user requests to share the session (copy link). */
  onShareSession?: (sessionKey: string) => void;
  /** Callback when user requests to export the session transcript. */
  onExportSession?: (sessionKey: string, format: SessionShareFormat) => void;
  /** Callback when user requests to preview the session. */
  onPreviewSession?: (sessionKey: string) => void;
  sessionChromeCollapsed?: boolean;
  onToggleSessionChromeCollapsed?: () => void;
  sessionNavigatorScrollable?: boolean;
  onToggleSessionNavigatorScrollable?: () => void;
  sessionNavigatorCanBack?: boolean;
  sessionNavigatorCanForward?: boolean;
  onSessionNavigatorBack?: () => void;
  onSessionNavigatorForward?: () => void;
  resetNodeBusy?: boolean;
  onResetNode?: () => void;
};

function renderSessionNavigatorScrollableToggle(props: FleetSessionBarProps) {
  if (!props.onToggleSessionNavigatorScrollable) {
    return nothing;
  }
  const scrollable = props.sessionNavigatorScrollable !== false;
  return html`
    <button
      type="button"
      class="fleet-session-bar__focus-btn fleet-session-bar__layout-btn"
      title=${
        scrollable
          ? FLEET_CHAT_FOCUS_HINTS.sessionNavigatorScrollableOff
          : FLEET_CHAT_FOCUS_HINTS.sessionNavigatorScrollableOn
      }
      aria-label=${scrollable ? "Use classic session layout" : "Use scrollable session layout"}
      aria-pressed=${scrollable}
      @click=${props.onToggleSessionNavigatorScrollable}
    >
      ${scrollable ? "Classic layout" : "Scrollable nav"}
    </button>
  `;
}

function renderSessionNavigatorHistoryButtons(props: FleetSessionBarProps) {
  if (!props.onSessionNavigatorBack && !props.onSessionNavigatorForward) {
    return nothing;
  }
  return html`
    <div class="fleet-session-bar__nav-history" role="group" aria-label="Session navigation history">
      <button
        type="button"
        class="fleet-session-bar__nav-btn fleet-icon-btn"
        ?disabled=${props.sessionNavigatorCanBack !== true}
        title="Previous session (agent / kind / id)"
        aria-label="Previous session"
        @click=${() => props.onSessionNavigatorBack?.()}
      >
        ${icons.arrowLeft}
      </button>
      <button
        type="button"
        class="fleet-session-bar__nav-btn fleet-icon-btn"
        ?disabled=${props.sessionNavigatorCanForward !== true}
        title="Next session (agent / kind / id)"
        aria-label="Next session"
        @click=${() => props.onSessionNavigatorForward?.()}
      >
        ${icons.arrowRight}
      </button>
    </div>
  `;
}

function renderResetNodeButton(props: FleetSessionBarProps) {
  if (!props.onResetNode) {
    return nothing;
  }
  return html`
    <button
      type="button"
      class="fleet-session-bar__reset-node-btn"
      ?disabled=${!props.connected || props.resetNodeBusy}
      title="Stop lingering subagents, sweep orphan sessions, and abort stuck chat runs on this gateway"
      aria-label="Reset node orchestration state"
      @click=${props.onResetNode}
    >
      ${props.resetNodeBusy ? "Resetting…" : "Reset Node"}
    </button>
  `;
}

function renderSessionChromeToggle(props: FleetSessionBarProps) {
  if (!props.onToggleSessionChromeCollapsed) {
    return nothing;
  }
  const collapsed = props.sessionChromeCollapsed === true;
  return html`
    <button
      type="button"
      class="fleet-session-bar__focus-btn"
      title=${
        collapsed
          ? FLEET_CHAT_FOCUS_HINTS.sessionChromeExpand
          : FLEET_CHAT_FOCUS_HINTS.sessionChromeCollapse
      }
      aria-label=${collapsed ? "Expand session navigator" : "Collapse session navigator"}
      aria-expanded=${!collapsed}
      @click=${props.onToggleSessionChromeCollapsed}
    >
      ${collapsed ? "▸ Session navigator" : "▾ Hide navigator"}
    </button>
  `;
}

/** Session chrome inside the Chat tab: open tabs + path navigator. */
export function renderFleetSessionBar(props: FleetSessionBarProps) {
  const collapsed = props.sessionChromeCollapsed === true;
  const badge = resolveFleetSessionBadge(props.activeSessionKey, props.sessions);

  if (collapsed) {
    return html`
      <div class="fleet-session-bar fleet-session-bar--collapsed" role="region" aria-label="Session (compact)">
        <div class="fleet-session-bar__compact">
          ${renderSessionNavigatorHistoryButtons(props)}
          ${renderResetNodeButton(props)}
          ${renderSessionNavigatorScrollableToggle(props)}
          ${renderSessionChromeToggle(props)}
          <span class="fleet-session-bar__compact-badge mono" title=${badge.title}>
            ${badge.short}
          </span>
          <span class="fleet-session-bar__compact-hint muted">Messages below</span>
        </div>
      </div>
    `;
  }

  return html`
    <div class="fleet-session-bar" data-testid="fleet-session-bar" role="region" aria-label="Session navigator">
      <div class="fleet-session-bar__header">
        <div class="fleet-session-bar__header-leading">
          ${renderSessionNavigatorHistoryButtons(props)}
          <span class="fleet-session-bar__header-label muted">Session navigator</span>
        </div>
        <div class="fleet-session-bar__header-actions">
          ${renderResetNodeButton(props)}
          ${renderSessionNavigatorScrollableToggle(props)}
          ${renderSessionChromeToggle(props)}
        </div>
      </div>
      <div class="fleet-session-bar__grow" data-testid="fleet-session-nav-grow">
        ${renderFleetSessionCatalogRow({
          sessionKey: props.activeSessionKey,
          openTabs: props.openTabs,
          sessions: props.sessions,
          connected: props.connected,
          tabLabelHints: props.labelHints,
          onSelectSession: props.onSelectTab,
          onNewEmptySession: props.onNewEmptySession,
          onArchiveAndNew: props.onArchiveAndNew,
          onManageSessions: props.onManageSessions,
          onShowArchivedSessions: props.onShowArchivedSessions,
          onForkTab: props.onForkTab,
          onNewAgent: props.onNewAgent,
          onOpenAgents: props.onOpenAgents,
          onShareSession: props.onShareSession,
          onExportSession: props.onExportSession,
          onPreviewSession: props.onPreviewSession,
        })}
        ${renderChatSessionTabs({
          activeSessionKey: props.activeSessionKey,
          openTabs: props.openTabs,
          sessions: props.sessions,
          labelHints: props.labelHints,
          highlightSessionKey: props.highlightSessionKey,
          tabActivity: props.tabActivity,
          connected: props.connected,
          onSelectTab: props.onSelectTab,
          onCloseTab: props.onCloseTab,
          onForkTab: props.onForkTab,
          onResetTab: props.onResetTab,
          onDeleteSession: props.onDeleteSession,
          onManageSessions: props.onManageSessions,
          rowLabel: "Open tabs",
        })}
      </div>
    </div>
  `;
}
